import {
  type ComponentProps,
  Fragment,
  memo,
  type ReactNode,
  useMemo
} from 'react';

import { Moon, Sun } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator
} from '@/shadcn/breadcrumb';
import { Button } from '@/shadcn/button';
import { cn } from '@/shadcn/lib/utils';
import { useTheme } from '@/shadcn/theme/theme-provider';

import { NavbarSection } from '@/components/layouts/header/navbarSection/navbarSection';
import { NavUser } from '@/components/layouts/header/user.navbar';
import { LogoStratumn } from '@/components/ui/customIcons/logoStratumn';
import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import { ProductionBadge } from '@/components/userDashboard/workflowsCards/ProductionBadge';
import { WorkspaceTag } from '@/components/userDashboard/workflowsCards/WorkspaceTag';
import { env } from '@/constant/env';
import { ROUTE_WORKFLOW_DASHBOARD } from '@/constant/routes';
import { useHasWorkspaces } from '@/hooks/useHasWorkspaces';
import { useUser } from '@/hooks/useUser';
import { useWorkspaceHighlight } from '@/hooks/useWorkspaceHighlight';
import { deepGet } from '@/utils';
import { BottomLink } from './bottomLink/bottomLink.header';
import { EnvPillWithBuildInfo } from './envPillWithBuildInfo';
import { NAVBAR_NARROW_HIDDEN } from './navbar.constants';

interface BottomLevel {
  workflowPage?: boolean;
  infoContext?: {
    links?: {
      icon?: string;
      label?: string;
      path?: string;
      node?: ReactNode;
    }[];
  };
  actions?: {
    links: ComponentProps<typeof BottomLink>[];
  };
}

export type NavbarConfig = {
  loading?: boolean;
  bottomLevel?: BottomLevel;
};

type Props = {
  config?: NavbarConfig;
  centerSlot?: ReactNode;
};

export const Navbar = memo(({ config, centerSlot }: Props) => {
  const loading = config?.loading ?? false;
  const bottomLevel = config?.bottomLevel ?? null;

  const currentEnv = env.VITE_ENVIRONMENT;

  const { isPrivileged } = useUser();
  const { setTheme, theme } = useTheme();

  // When no explicit center slot is provided, derive a read-only badge from the
  // current route: the workspace tag if the route's workflow/group/trace has a
  // workspace, otherwise a "Production" badge on workspace-scoped routes — but
  // only for users who actually have workspace access. The dashboard passes its
  // own filter dropdown as `centerSlot`, which takes precedence.
  const {
    workspace,
    isWorkspaceRoute,
    loading: workspaceLoading,
    error: workspaceError,
    wash: highlightWash
  } = useWorkspaceHighlight();
  const hasWorkspaces = useHasWorkspaces(isWorkspaceRoute);

  const autoCenterSlot = useMemo(() => {
    if (workspace) {
      return (
        <WorkspaceTag
          badgeClassName={NAVBAR_NARROW_HIDDEN}
          color={workspace.color}
          name={workspace.name}
          version={workspace.version}
        />
      );
    }
    // Don't claim "Production" when the lookup actually failed — a transient
    // error shouldn't be presented as a definitive "no workspace" state.
    if (
      isWorkspaceRoute &&
      !workspaceLoading &&
      !workspaceError &&
      hasWorkspaces
    ) {
      return <ProductionBadge />;
    }
    return null;
  }, [
    workspace,
    isWorkspaceRoute,
    workspaceLoading,
    workspaceError,
    hasWorkspaces
  ]);
  const resolvedCenterSlot = centerSlot ?? autoCenterSlot;

  const onChangeTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const renderSubNavbarLeftSide = useMemo(() => {
    if (!deepGet(bottomLevel, 'infoContext.links')) return null;
    if (!bottomLevel?.infoContext?.links) return null;
    if (loading) return null;

    const items = [
      {
        icon: 'lucide_House',
        label: 'Home',
        path: ROUTE_WORKFLOW_DASHBOARD
      },
      ...bottomLevel.infoContext.links
    ];

    return (
      <Breadcrumb>
        <BreadcrumbList className="text-[13px]">
          {items.map((item, index) => (
            <Fragment key={index}>
              <BreadcrumbItem key={item.label}>
                <BreadcrumbLink asChild href={item.path}>
                  {item.path ? (
                    <Link
                      className="flex h-full items-center gap-2"
                      to={item.path}
                    >
                      {item.node}
                      {item.icon && <DynamicIcon icon={item.icon} size={16} />}
                      <span className="pt-0.5">{item.label}</span>
                    </Link>
                  ) : (
                    <div className="flex h-full cursor-default items-center gap-2 hover:text-muted-foreground">
                      {item.node}
                      {item.icon && <DynamicIcon icon={item.icon} size={16} />}
                      <span className="pt-0.5">{item.label}</span>
                    </div>
                  )}
                </BreadcrumbLink>
              </BreadcrumbItem>
              {index !== items.length - 1 && <BreadcrumbSeparator />}
            </Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    );
  }, [!loading && deepGet(bottomLevel, 'infoContext.links')]);

  const renderSubNavbarRightSide = useMemo(() => {
    if (!(bottomLevel && bottomLevel.actions)) return;

    const { links } = bottomLevel.actions;

    return (
      <div className="flex flex-row items-center text-muted-foreground text-xs">
        {links?.map((link, index) => (
          <Fragment key={index}>
            <BottomLink {...link} />
          </Fragment>
        ))}
      </div>
    );
  }, [bottomLevel && bottomLevel.actions]);

  const hasSubNavbar = !!renderSubNavbarLeftSide || !!renderSubNavbarRightSide;

  return (
    <header
      className="sentry-unmask @container/navbar h-navbar w-full"
      data-cy="atomic-navbar"
    >
      <div className="fixed top-0 right-0 left-0 z-4 w-full bg-card text-card-foreground">
        <div
          className="relative flex h-navbar w-full items-center border-border border-b pt-(--workspace-frame-headroom) pr-5 pl-2.5"
          data-has-subheader={hasSubNavbar}
          data-testid="navbar-workspace-highlight"
          style={
            // Header highlight: a translucent wash of the active workspace
            // color over the navbar background, matching the screen border.
            // Sanitized by the shared hook before landing in the CSS value.
            highlightWash ? { backgroundColor: highlightWash } : undefined
          }
        >
          <Link
            aria-label="Go to the home page"
            className="p-1.5 pr-[17px] pb-3 pl-3.5 text-primary-deep hover:brightness-80 dark:text-card-foreground"
            data-cy="go-home"
            to={ROUTE_WORKFLOW_DASHBOARD}
          >
            <LogoStratumn axis="y" size={24} />
          </Link>
          <div className="flex min-w-0 grow justify-between">
            <NavbarSection className="order-1 @max-[470px]/navbar:hidden max-w-7/10 gap-2 overflow-hidden">
              <Button variant="link">
                <Link
                  className={cn(
                    'flex h-full shrink-0 cursor-pointer select-none items-center justify-center font-semibold text-base text-primary-deep tracking-wider underline decoration-2 underline-offset-4 hover:text-primary-deep/80'
                  )}
                  data-cy="atomic-navbar-link"
                  draggable="false"
                  to={ROUTE_WORKFLOW_DASHBOARD}
                >
                  Trace
                </Link>
              </Button>
              <Button variant="link">
                <a
                  className={cn(
                    'flex h-full shrink-0 cursor-pointer select-none items-center justify-center font-medium text-base text-card-foreground tracking-wider no-underline decoration-2 underline-offset-4 hover:text-primary-deep hover:underline'
                  )}
                  data-cy="atomic-navbar-link"
                  draggable="false"
                  href={env.VITE_ANALYTICS_URL}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Analytics
                </a>
              </Button>
              {!currentEnv.includes('release') && (
                <EnvPillWithBuildInfo
                  allowedEnvs={['local', 'staging', 'preprod', 'preprod-us']}
                  className={cn(
                    'ml-3 cursor-default select-none rounded-full bg-primary-deep/80 px-3 font-semibold text-primary-foreground text-sm',
                    NAVBAR_NARROW_HIDDEN
                  )}
                  env={currentEnv}
                />
              )}
            </NavbarSection>
            {resolvedCenterSlot && (
              <NavbarSection className="order-2 @max-[470px]/navbar:ml-2 ml-6 flex min-w-0 shrink items-center justify-center">
                {resolvedCenterSlot}
              </NavbarSection>
            )}
            <NavbarSection
              className="order-3 ml-auto justify-end gap-4 only:w-full"
              loading={loading}
            >
              {/* <FeedbackButton /> */}
              {isPrivileged && (
                <Button onClick={onChangeTheme} size="icon" variant="ghost">
                  <Sun className="dark:-rotate-90 h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:scale-0" />
                  <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                  <span className="sr-only">Toggle theme</span>
                </Button>
              )}
              <NavUser />
            </NavbarSection>
          </div>
        </div>
        {hasSubNavbar && (
          <div
            className="flex h-subnavbar w-full items-center border-border border-b bg-accent px-5"
            data-cy="atomic-subnavbar"
          >
            <div
              className={cn(
                'flex grow justify-between opacity-100 transition-opacity',
                loading && 'opacity-0'
              )}
            >
              {renderSubNavbarLeftSide && (
                <NavbarSection className="order-1 mr-5 max-w-7/10 overflow-hidden">
                  {renderSubNavbarLeftSide}
                </NavbarSection>
              )}
              {renderSubNavbarRightSide && (
                <NavbarSection className="order-3 justify-end only:w-full">
                  {renderSubNavbarRightSide}
                </NavbarSection>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
});
